import { useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useSession } from '@/hooks/useSession';
import {
  useQariMutations,
  useQariProfile,
} from '@/hooks/useQariProfile';
import { OnboardingWizard } from '@/components/qari/OnboardingWizard';
import {
  QARI_EDITABLE_STATUSES,
  type QariProfile,
} from '@/types/qari';

/**
 * /qari/onboarding — Multi-step wizard.
 *
 * On first visit (no profile row yet), creates a draft row via
 * upsertQariDraft so the wizard always has a profile to bind to.
 */
export function QariOnboarding() {
  useDocumentTitle('Qari · Onboarding');

  const session = useSession();
  const profile = useQariProfile();
  const { saveDraft } = useQariMutations();
  const navigate = useNavigate();

  // First-visit bootstrap: if signed-in and no profile row exists yet,
  // create an empty draft so the wizard can save into it. We do this
  // once per mount. Gated on profile.isSuccess so we don't fire while
  // the query is still in flight or errored.
  useEffect(() => {
    if (
      session.data &&
      profile.isSuccess &&
      !profile.data &&
      !saveDraft.isPending &&
      !saveDraft.isError
    ) {
      saveDraft.mutate({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.data?.user.id, profile.isSuccess, profile.data]);

  if (profile.isLoading) {
    return (
      <section className="container-prose pt-12 pb-16">
        <p className="text-ink-500 dark:text-ink-400 text-sm">Loading…</p>
      </section>
    );
  }

  if (profile.isError) {
    return (
      <section className="container-prose pt-12 pb-16">
        <p className="text-xs uppercase tracking-[0.22em] text-rose-600 dark:text-rose-400 mb-3">
          Qari · Onboarding
        </p>
        <h1 className="font-serif text-display-2 text-ink-900 dark:text-ink-50">
          Couldn't load your profile
        </h1>
        <p className="text-base text-ink-600 dark:text-ink-300 leading-relaxed mt-3 max-w-prose">
          {profile.error instanceof Error
            ? profile.error.message
            : 'Unknown error.'}
        </p>
        <div className="mt-6">
          <Button variant="secondary" onClick={() => profile.refetch()}>
            Try again
          </Button>
        </div>
      </section>
    );
  }

  // If we successfully fetched and there is no profile yet, the
  // bootstrap effect above will create one. While that mutation is in
  // flight, render a calm Setting-up state. Surface bootstrap errors.
  if (!profile.data) {
    if (saveDraft.isError) {
      return (
        <section className="container-prose pt-12 pb-16">
          <p className="text-xs uppercase tracking-[0.22em] text-rose-600 dark:text-rose-400 mb-3">
            Qari · Onboarding
          </p>
          <h1 className="font-serif text-display-2 text-ink-900 dark:text-ink-50">
            Couldn't start your draft
          </h1>
          <p className="text-base text-ink-600 dark:text-ink-300 leading-relaxed mt-3 max-w-prose">
            {saveDraft.error instanceof Error
              ? saveDraft.error.message
              : 'Unknown error.'}
          </p>
          <div className="mt-6">
            <Button
              variant="secondary"
              onClick={() => saveDraft.mutate({})}
            >
              Try again
            </Button>
          </div>
        </section>
      );
    }
    return (
      <section className="container-prose pt-12 pb-16">
        <p className="text-ink-500 dark:text-ink-400 text-sm">
          Setting up your draft…
        </p>
      </section>
    );
  }

  // Submitted-already: nudge to dashboard. We don't let users keep
  // editing a profile that's mid-review.
  if (!QARI_EDITABLE_STATUSES.has(profile.data.status)) {
    return <Navigate to="/qari" replace />;
  }

  return (
    <section className="container-page pt-10 sm:pt-14 pb-16 max-w-3xl">
      <Header profile={profile.data} />
      <OnboardingWizard
        profile={profile.data}
        onSubmitted={() => navigate('/qari')}
      />
    </section>
  );
}

function Header({ profile }: { profile: QariProfile }) {
  return (
    <header className="mb-8">
      <p className="text-xs uppercase tracking-[0.22em] text-accent-600 dark:text-accent-400 mb-3">
        Qari onboarding
      </p>
      <h1 className="font-serif text-display-2 text-ink-900 dark:text-ink-50">
        Set up your profile
      </h1>
      <p className="text-base text-ink-600 dark:text-ink-300 mt-3 max-w-prose leading-relaxed">
        Walk through four short steps. Your work auto-saves as you go,
        so you can leave and come back at any time. Submit when you're
        ready and we'll review your profile.
      </p>
      {profile.status === 'rejected' && (
        <div className="mt-5">
          <Button variant="ghost" size="sm" disabled>
            Editing after review feedback
          </Button>
        </div>
      )}
    </header>
  );
}
